import { useCallback, useEffect, useState } from 'react'

import * as PIXI from 'pixi.js'

import { useMapEventBus } from 'modules/space_control/contexts/workSpacesMap/MapEventBusContext'
import { WorkAreaMapLocationNode } from 'modules/space_control/hooks/useWorkAreaMap'
import { usePIXI } from 'modules/space_control/lib/canvas'
import { Viewport } from 'modules/space_control/lib/canvas/viewport'
import {
  EVENT,
  UpdateWorkspacesLocationsEvent,
} from 'modules/space_control/lib/workSpacesMap/mapEvents'

import { Pin } from './pin.pixi'

interface AppProps {
  mapImage: { url?: string | null } | null | undefined
  onViewportReady?: (viewport: PIXI.Container) => void
}

export const App = ({ mapImage, onViewportReady }: AppProps) => {
  const app = usePIXI()
  const [pinsContainer, setPinsContainer] = useState<PIXI.Container>()
  const [viewport, setViewport] = useState<PIXI.Container | null>(null)
  const [currentWorkSpacesLocations, setCurrentWorkSpacesLocations] = useState<
    WorkAreaMapLocationNode[]
  >([])
  const { eventBus, setReady } = useMapEventBus()

  useEffect(() => {
    if (!eventBus) return

    const handleUpdateWorkspacesLocations = ({
      workAreaMapLocations,
    }: UpdateWorkspacesLocationsEvent) => {
      setCurrentWorkSpacesLocations(workAreaMapLocations)
    }

    eventBus.on(EVENT.UPDATE_WORKSPACES, handleUpdateWorkspacesLocations)
    setReady(true)

    return () => {
      eventBus.off(EVENT.UPDATE_WORKSPACES, handleUpdateWorkspacesLocations)
      setReady(false)
    }
  }, [eventBus, setReady])

  useEffect(() => {
    if (!app || !mapImage || !mapImage?.url) return

    const viewportMask = new PIXI.Graphics()
    viewportMask.beginFill(0x000000, 0)
    viewportMask.drawRect(0, 0, app.screen.width, app.screen.height)
    viewportMask.endFill()

    viewportMask.interactive = true
    viewportMask.hitArea = new PIXI.Rectangle(0, 0, app.screen.width, app.screen.height)

    app.stage.addChild(viewportMask)
    app.stage.interactiveChildren = true

    const newViewport = Viewport(app, {
      isPanEnabled: true,
      isPinchZoomEnabled: true,
      isWheelZoomEnabled: true,
    })

    const image = PIXI.Sprite.from(mapImage.url)
    image.anchor.set(0.5)
    newViewport.addChild(image)

    const pins = new PIXI.Container()
    setPinsContainer(pins)
    newViewport.addChild(pins)

    viewportMask.addChild(newViewport)
    setViewport(newViewport)
    onViewportReady?.(newViewport)

    return () => {
      newViewport.unbind()
      newViewport.destroy({ children: true })
    }
  }, [app, mapImage, onViewportReady])

  useEffect(() => {
    if (!viewport || !eventBus || !app?.view) return

    const canvasElement = app.view
    if (!(canvasElement instanceof HTMLCanvasElement)) return

    const activateCursor = () => {
      viewport.cursor = 'move'
      canvasElement.style.cursor = 'move'
    }

    const resetCursor = () => {
      viewport.cursor = 'default'
      canvasElement.style.cursor = 'default'
    }

    eventBus.on(EVENT.ACTIVATE_MOVE_CURSOR, activateCursor)
    eventBus.on(EVENT.RESET_CURSOR, resetCursor)

    return () => {
      eventBus.off(EVENT.ACTIVATE_MOVE_CURSOR, activateCursor)
      eventBus.off(EVENT.RESET_CURSOR, resetCursor)
    }
  }, [viewport, eventBus, app?.view])

  useEffect(() => {
    if (!pinsContainer || !currentWorkSpacesLocations || !eventBus) return

    pinsContainer.removeChildren()

    currentWorkSpacesLocations.forEach((workSpaceLocation) => {
      if (!workSpaceLocation) return

      const pin = Pin(workSpaceLocation, {
        onClick: (workSpaceLocationId) => {
          eventBus.emit(EVENT.SPACE_CLICK, {
            workAreaMapLocation:
              currentWorkSpacesLocations.find((location) => location.id === workSpaceLocationId) ||
              null,
          })
        },
        onHover: (workSpaceLocationId) => {
          eventBus.emit(EVENT.SPACE_HOVER, {
            workAreaMapLocation:
              currentWorkSpacesLocations.find((location) => location.id === workSpaceLocationId) ||
              null,
          })
        },
        onLeave: () => {
          eventBus.emit(EVENT.SPACE_HOVER, { workAreaMapLocation: null })
        },
      })
      if (!pin) return

      pin.x = workSpaceLocation.x
      pin.y = workSpaceLocation.y
      pinsContainer.addChild(pin)
    })

    return () => {
      pinsContainer.removeChildren()
    }
  }, [currentWorkSpacesLocations, pinsContainer, eventBus])

  const handleViewportClick = useCallback(
    (event: PIXI.FederatedPointerEvent) => {
      if (event.button === 0 && viewport && eventBus) {
        const point = viewport.toLocal(event.global)
        eventBus.emit(EVENT.MAP_CLICK, { x: point.x, y: point.y })
      }
    },
    [viewport, eventBus]
  )

  useEffect(() => {
    if (!viewport) return

    viewport.on('click', handleViewportClick)

    return () => {
      viewport.off('click', handleViewportClick)
    }
  }, [viewport, handleViewportClick])

  return null
}
